import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, ApiError, type Me } from './api';

interface SessionState {
  loading: boolean;
  me: Me | null;
  productName: string;
  refresh: () => Promise<Me | null>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState<Me | null>(null);
  const [productName, setProductName] = useState('Kruvi Access');

  const refresh = useCallback(async () => {
    try {
      const next = await api<Me>('/api/auth/me');
      setMe(next);
      return next;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setMe(null);
        return null;
      }
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    await api('/api/auth/logout', { method: 'POST' });
    setMe(null);
  }, []);

  useEffect(() => {
    api<{ productName: string }>('/api/meta').then((m) => {
      setProductName(m.productName);
      document.title = m.productName;
    }).catch(() => undefined);
    void refresh();
  }, [refresh]);

  return <SessionContext.Provider value={{ loading, me, productName, refresh, signOut }}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession outside SessionProvider');
  return ctx;
}
